import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';

Vue.use(Vuex);

const store = new Vuex.Store({
  getters: {
    isAuthenticated: state => state.auth.token !== '',
    authStatus: state => state.auth.status,
  },

  state: {
    firstConnect: true,
    busy: false,
    auth: {
      token: localStorage.getItem('user-token') ? JSON.parse(localStorage.getItem('user-token')) : '',
      status: '',
    },
    favorites: [],
    alert: {
      text: '',
      visible: false,
    },
    latestSoftwareVersion: {
      semantic: '0.0.0',
      meta: '',
    },
    latestGtpsSoftwareVersion: {
      semantic: '0.0.0',
      meta: '',
    },
    latestOasisSoftwareVersion: {
      semantic: '0.0.0',
      meta: '',
    },

    stagedUpdateTotals: {
      total: 0,
      versions: {
      },
    },
    stagedGtpsUpdateTotals: {
      total: 0,
      versions: {
      },
    },

    connectionCounters: {
      connectedAligners: 0,
      totalAligners: 0,
      connectedUsers: 0,
    },
    connectedAlignersTrend: [],

    alignerSoftwareVersions: [],
    gtpsSoftwareVersions: [],
    oasisSoftwareVersions: [],
    specVersions: [],

    supportTicketStatistics: {
      openTicketCount: 0,
      closedTicketCount: 0,
      totalTickets: 0,
      closedTicketsByIssuerUsername: [],
      openTicketsByIssuerUsername: [],
      totalTicketsByIssuerUsername: [],
    },
    top10Aligners: [],

    serviceStatus: {
      silvio: false,
      charon: false,
      dalton: false,
      aligners: false,
      softwareUpdates: false,
      gtpsSoftwareUpdates: false,
      radar: false,
      cameralCal: false,
      easy: false,
      truPoint: false,
      dashboard: false,
      websockets: false,
    },

  },

  mutations: {
    setFavorites(state, payload) {
      state.favorites = payload;
    },

    updateFavorite(state, payload) {
      const { serialNumber, ...rest } = payload;

      state.favorites = state.favorites.map((cur) => {
        if (cur.serialNumber === serialNumber) {
          return Object.assign({}, cur, rest);
        }

        return cur;
      });

      // console.log(JSON.stringify(state.favorites, null, 2));
    },

    removeFavorite(state, serialNumber) {
      state.favorites = state.favorites.filter(cur => cur.serialNumber !== serialNumber);
    },

    addFavorite(state, aligner) {
      state.favorites.push(aligner);
    },

    setAlertVisible(state, value) {
      state.alert.visible = value;
    },

    setAlertText(state, value) {
      state.alert.text = value;
      state.alert.visible = true;
    },

    setLatestSoftwareVersion(state, value) {
      state.latestSoftwareVersion = value;
    },

    setLatestGtpsSoftwareVersion(state, value) {
      state.latestGtpsSoftwareVersion = value;
    },

    setLatestOasisSoftwareVersion(state, value) {
      state.latestOasisSoftwareVersion = value;
    },

    setAuthToken(state, message) {
      state.auth.token = message;
    },

    setStagedUpdateTotals(state, message) {
      state.stagedUpdateTotals = message;
    },

    setStagedGtpsUpdateTotals(state, message) {
      state.stagedGtpsUpdateTotals = message;
    },

    setStagedOasisUpdateTotals(state, message) {
      state.stagedOasisUpdateTotals = message;
    },

    setAlignerSoftwareVersions(state, message) {
      state.alignerSoftwareVersions = message;
    },

    setGtpsSoftwareVersions(state, message) {
      state.gtpsSoftwareVersions = message;
    },

    setOasisSoftwareVersions(state, message) {
      state.oasisSoftwareVersions = message;
    },

    setSpecVersions(state, message) {
      state.specVersions = message;
    },

    setServiceStatus(state, message) {
      state.serviceStatus = message;
    },

    setBusy(state, message) {
      state.busy = message;
    },

    setConnectionCounters(state, message) {
      // console.log(`state.connectionCounters = ${JSON.stringify(message)}`);
      state.connectionCounters = message;

      state.connectedAlignersTrend.push(message.connectedAligners);
      if (state.connectedAlignersTrend.length > 60) {
        state.connectedAlignersTrend.shift();
      }
    },

    setSupportTicketStatistics(state, message) {
      state.supportTicketStatistics = message;
    },

    setTopTenAligners(state, message) {
      state.top10Aligners = message;
    },
  },

  actions: {
    setLeftFrontCamber(context, value) {
      // console.log(`*** action setLeftFrontCamber ${value} ***`);
      context.commit('setLeftFrontCamber', value);
    },

    getFavorites(context) {
      const options = {
        method: 'get',
        url: `/read/favorites/v1/?username=${context.state.auth.token.user.username}`,
      };

      return axios(options)
        .then((res) => {
          context.commit('setFavorites', res.data);
        })
        .catch((err) => {
        });
    },

    async addFavorite(context, serialNumber) {
      const aligner = await context.dispatch('getAlignerBySerialNumber', serialNumber);
      context.commit('addFavorite', aligner);
    },

    removeFavorite(context, serialNumber) {
      context.commit('removeFavorite', serialNumber);
    },

    updateServiceStatus(context, data) {
      context.commit('setServiceStatus', data);
    },

    updateFavorite(context, data) {
      context.commit('updateFavorite', data);
    },

    getAlignerBySerialNumber(context, serialNumber) {
      return new Promise((resolve, reject) => {
        const options = {
          method: 'get',
          url: `/read/aligner/v1/?serialNumber=${serialNumber.toUpperCase()}&username=${context.state.auth.token.user.username}`,
        };

        axios(options)
          .then((res) => {
            resolve(res.data);
          })
          .catch((err) => {
            reject();
          });
      });
    },

    getServiceStatus(context) {
      const options = {
        method: 'GET',
        url: '/api/v1/check_services',
      };

      return axios(options)
        .then((res) => {
          context.commit('setServiceStatus', res.data);
        })
        .catch(() => {

        });
    },

    getLatestSoftwareVersion(context) {
      const options = {
        method: 'GET',
        url: '/read/latest_software_version/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setLatestSoftwareVersion', res.data);
        })
        .catch(() => {

        });
    },

    getLatestGtpsSoftwareVersion(context) {
      const options = {
        method: 'GET',
        url: '/read/latest_gtps_software_version/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setLatestGtpsSoftwareVersion', res.data);
        })
        .catch(() => {
          log.error({ message: 'read/latest_gtps_software_version/v1 FAILED!!!!' });
        });
    },

    getLatestOasisSoftwareVersion(context) {
      const options = {
        method: 'GET',
        url: '/read/latest_oasis_software_version/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setLatestOasisSoftwareVersion', res.data);
        })
        .catch(() => {
          log.error({ message: 'read/latest_oasis_software_version/v1 FAILED!!!!' });
        });
    },

    getStagedUpdateTotals(context) {
      const options = {
        method: 'GET',
        url: '/read/staged_update_totals/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setStagedUpdateTotals', res.data);
        })
        .catch(() => {

        });
    },

    getStagedGtpsUpdateTotals(context) {
      const options = {
        method: 'GET',
        url: '/read/staged_gtps_update_totals/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setStagedGtpsUpdateTotals', res.data);
        })
        .catch(() => {

        });
    },

    getStagedOasisUpdateTotals(context) {
      console.log('[Oasis] getStagedOasisUpdateTotals');

      const options = {
        method: 'GET',
        url: '/read/staged_oasis_update_totals/v1',
      };

      return axios(options)
        .then((res) => {
          console.log('[Oasis] getStagedOasisUpdateTotals SUCCESSFULL!');
          console.log(JSON.stringify(res.data));

          context.commit('setStagedOasisUpdateTotals', res.data);
        })
        .catch(() => {
          console.log('[Oasis] getStagedOasisUpdateTotals FAILED!!!!');
        });
    },

    getAlignerSoftwareVersions(context) {
      const options = {
        method: 'GET',
        url: '/read/aligner_software_versions/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setAlignerSoftwareVersions', res.data);
        })
        .catch(() => {

        });
    },

    getGtpsSoftwareVersions(context) {
      const options = {
        method: 'GET',
        url: '/read/gtps_software_versions/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setGtpsSoftwareVersions', res.data);
        })
        .catch(() => {

        });
    },

    getOasisSoftwareVersions(context) {
      const options = {
        method: 'GET',
        url: '/read/oasis_software_versions/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setOasisSoftwareVersions', res.data);
        })
        .catch(() => {

        });
    },

    getSpecVersions(context) {
      const options = {
        method: 'GET',
        url: '/read/spec_versions/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setSpecVersions', res.data);
        })
        .catch(() => {

        });
    },

    getSupportTicketStatistics(context) {
      const options = {
        method: 'GET',
        url: '/read/ticket_statistics/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setSupportTicketStatistics', res.data);
        })
        .catch(() => {

        });
    },

    getTop10Aligners(context) {
      const options = {
        method: 'GET',
        url: '/read/top_aligners_by_alignment_count/v1',
      };

      return axios(options)
        .then((res) => {
          context.commit('setTopTenAligners', res.data);
        })
        .catch(() => {

        });
    },
  },
});

export default store;
